<template>
  <v-dialog
    :value="visiblePopup"
    @keydown.esc="onYesClickButton"
    @click:outside='onYesClickButton'
    :max-width="730"
  >
    <v-card class="popup-wrapper">
      <div class='popup-wrapper__action'>
        <span class='close_btn' @click='onYesClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class="title">{{$t(title)}}</div>
      <div class="data-row">
        <div class="details-column org">
          <div class="data-title">{{$t('host.asn.popup.org')}}</div>
          <div class="data-text"> {{data.org}} </div>
        </div>
      </div>
      <div class="data-row">
        <div class="details-column org">
          <div class="data-title">{{$t('host.asn.popup.ip')}}</div>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.ip}"
            :value="displayedCopiedBadge.ip"
          >
            <div class="data-text" @click='copyAsset(data.ip, "ip")'>{{data.ip}}</div>
          </v-badge>
        </div>
      </div>
      <div class="data-row">
        <div class="details-column asn">
          <div class="data-title">{{$t('host.asn.popup.asn')}}</div>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.asn}"
            :value="displayedCopiedBadge.asn"
          >
            <div class="data-text" @click='copyAsset(data.asn, "asn")'>{{data.asn}}</div>
          </v-badge>
        </div>
        <div class="details-column network">
          <div class="data-title">{{$t('host.asn.popup.network')}}</div>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.network}"
            :value="displayedCopiedBadge.network"
          >
            <div class="data-text" @click='copyAsset(data.network, "network")'>{{data.network}}</div>
          </v-badge>
        </div>
        <div class="details-column version">
          <div class="data-title">{{$t('host.asn.popup.version')}}</div>
          <div class="data-text"> {{data.version}} </div>
        </div>
      </div>
      <div class="data-row">
        <div class="details-column region">
          <div class="data-title">{{$t('host.asn.popup.region')}}</div>
          <div class="data-text"> {{data.region}} </div>
        </div>
        <div class="details-column country">
          <div class="data-title">{{$t('host.asn.popup.country.name')}}</div>
          <div class="data-text"> {{data.country_name}} </div>
        </div>
        <div class="details-column city">
          <div class="data-title">{{$t('host.asn.popup.city')}}</div>
          <div class="data-text"> {{data.city}} </div>
        </div>
        <div class="details-column tld">
          <div class="data-title">{{$t('host.asn.popup.tld.extension')}}</div>
          <div class="data-text"> {{getFormattedExtension}} </div>
        </div>
      </div>
      <div class="buttons">
        <v-btn small depressed class="yes-button btn-primary" @click="onYesClickButton()">
          {{$t(yesCaption)}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>

</template>

<script>

import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'AsnPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    title: String,
    data: {
      required: false,
      type: Object,
      default: () => {},
    },
    yesCaption: String,
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  methods: {
    onYesClickButton() {
      this.$emit('clicked', true);
    },
    async copyAsset(asset, type) {
      const data = asset;
      await navigator.clipboard.writeText(data);
      this.displayedCopiedBadge[type] = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge[type] = false;
    },
  },
  data() {
    return {
      displayedCopiedBadge: {
        asn: false,
        ip: false,
        network: false,
      },
    };
  },
  computed: {
    getFormattedExtension() {
      return this.data.country_tld.startsWith('.') ? this.data.country_tld.slice(1) : this.data.country_tld;
    },
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  max-width: rem(730);
  padding: $space-8 $space-10;
  background-color: var(--bg-primary);
  &__action {
    .close_btn {
      margin-right: rem(-20);
      margin-top: rem(-12);
      float: right;

      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }
  .title {
    color: var(--text-primary);
    font-size: $font-size-xl;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: rem(24);
    text-transform: uppercase;
    padding-bottom: rem(23);
  }
  .data-row {
    margin-top: rem(25);
    padding-bottom: $space-4;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    .details-column {
      display: flex;
      flex-direction: column;
      min-width: fit-content;
      .data-title {
        display: flex;
        color: var(--text-secondary);
        font-size: $font-size-sm;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        text-transform: uppercase;
        margin-bottom: $space-4;
      }
      .data-text {
        display: flex;
        overflow-wrap: break-word;
      }
      .manual-config-badge {
        max-width: fit-content;
        line-height: 1.5;
        &:hover {
          cursor: pointer;
        }
        .copied {
          text-transform: capitalize;
          color: var(--brand-primary);
        }
      }
    }
    .city {
      margin-left: rem(30);
    }
    .network, .country {
      margin-left: rem(50);
      min-width: rem(140);
    }
    .version {
      margin-left: rem(30);
    }
    .asn, .region {
      min-width: rem(120);
    }
    .tld {
     margin-left: rem(75);
    }
  }

  .buttons {
    text-align: right;
    padding-top: rem(30);

    .yes-button {
      height: $space-8;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary) !important;
      box-shadow: $shadow-sm;
      color: var(--text-inverse) !important;
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $space-4;
      text-align: center;
      margin-left: $space-2;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.popup-wrapper {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge.v-badge__badge {
        background: transparent;
        border: none;
        color: var(--brand-primary);
        .v-icon {
          font-size: $space-4;
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge.v-badge__badge {
          text-transform: capitalize;
          background: transparent;
          border: none;
          color: var(--brand-primary);
          right: $space-5;
          inset: auto auto calc(100% - 1px) calc(100% - #{rem(30)});
        }
      }
    }
  }
}
</style>
